import { Box, Text, TextButton } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus } from '@wix/rules-panel-ui';
import React, { useCallback, useMemo } from 'react';
import { BiCtaNames } from '../../../../bi/types';
import { usePanelStateAtom } from '../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../../../hooks/use-open-article-by-editor-host';
import { useGlobalSectionState } from '../../../../rule-mappers/shared/use-global-section-state';
import { useVerify } from '../../../../rule-mappers/shared/use-verify';
import { MAIN_KB_ARTICLE } from '../constants';
import { getKbArticleKey } from '../utils/kb-article-utils';
import { ElementWithContent } from './element-with-content';
import { ElementWithoutContent } from './element-without-content';
import { InaccessibleComponentI18nKeys } from './translation-keys';
export const InaccessibleComponentsElementContent = _ref => {
  let {
    element,
    status,
    elementIndex,
    pageId
  } = _ref;
  const {
    editorHost,
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const customMemberPage = a11yWizardAdapter.getMembersCustomPages();
  const isCustomMemberPage = pageId === customMemberPage.signIn.id || pageId === customMemberPage.signUp.id;
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    getGroupData,
    panelState
  } = usePanelStateAtom();
  const {
    verify
  } = useVerify({
    disableAxe: isCustomMemberPage
  });
  const {
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    getIsComponentInGlobalSection
  } = useGlobalSectionState();
  const kbArticleKey = useMemo(() => getKbArticleKey(element.type, element.tpaId) ?? MAIN_KB_ARTICLE, [element.type, element.tpaId]);
  const showElementWithContent = element.reinstall || element.alternateComponent && !element.followArticle;
  const handleRescanClick = useCallback(async componentId => {
    const groupData = getGroupData(pageId);
    if (!groupData) {
      throw new Error(`Group data not found for pageId: ${pageId}.`);
    }
    const isComponentInGlobalSection = getIsComponentInGlobalSection(componentId);
    await verify(groupData, [...getGlobalSectionModifiedComponentIds(panelState.globalSectionComponentsDataMap), ...(isComponentInGlobalSection ? [componentId] : [])]);
  }, [getGlobalSectionModifiedComponentIds, getGroupData, getIsComponentInGlobalSection, pageId, panelState.globalSectionComponentsDataMap, verify]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP4",
    marginTop: "SP1",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2",
    padding: "0 SP1"
  }, status === ElementStatus.Failed ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    secondary: true
  }, t(InaccessibleComponentI18nKeys.subtitle)), /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "B60",
    borderRadius: "6px",
    padding: "SP2"
  }, showElementWithContent ? /*#__PURE__*/React.createElement(ElementWithContent, {
    element: element,
    elementIndex: elementIndex,
    handleRescanClick: () => handleRescanClick(element.id),
    kbArticleKey: kbArticleKey,
    status: status
  }) : /*#__PURE__*/React.createElement(ElementWithoutContent, {
    element: element,
    elementIndex: elementIndex,
    handleRescanClick: () => handleRescanClick(element.id),
    kbArticleKey: kbArticleKey,
    status: status
  }))) : /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    secondary: true
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      learnMore: /*#__PURE__*/React.createElement(TextButton, {
        as: "a",
        size: "tiny",
        skin: "dark",
        target: "_blank",
        underline: "always",
        onClick: () => {
          openSelectedArticle(editorHost, MAIN_KB_ARTICLE);
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.LearnMore,
            index: elementIndex,
            status
          });
        }
      })
    },
    i18nKey: InaccessibleComponentI18nKeys.passedSubtitle
  }))));
};
//# sourceMappingURL=inaccessible-components-element-content.js.map